import { Rate, BankrateSelectedRateAnswer } from '@mortgage-pos/types'
import { rateCodesMap } from '@mortgage-pos/data'
import { deriveLoanTermInMonths } from '@mortgage-pos/utils'
import {
  LoanCostsCalculator,
  extractFeeByDescription,
} from '@mortgage-pos/utils'

export interface MatchedRates {
  rates: Rate[]
  rateItems: string[]
}

export interface RatesDetails {
  bankrateSelectedRate?: BankrateSelectedRateAnswer
  remainingTermCount?: number
  remainingBalance?: number
  sortedRates: Rate[]
}

export class WhyRefiPricingLogic {
  private bankrateSelectedRate: BankrateSelectedRateAnswer
  private matchedRates: MatchedRates
  private recommendedTerm: number
  private refiGoal: string
  private remainingTermCount: number
  private remainingBalance: number
  private sortedRates: Rate[]

  constructor(refiGoal: string, ratesDetails: RatesDetails) {
    this.bankrateSelectedRate = ratesDetails.bankrateSelectedRate
    this.sortedRates = ratesDetails.sortedRates ?? []
    this.remainingTermCount = ratesDetails.remainingTermCount ?? 30
    this.remainingBalance = ratesDetails.remainingBalance
    this.refiGoal = refiGoal
    this.matchedRates = { rates: [], rateItems: [] }
    this.recommendedTerm = this.findRecommendedTerm()
  }

  public findRecommendedTerm(): number {
    const closestMultipleOfFive = Math.round(this.remainingTermCount / 5) * 5

    if (closestMultipleOfFive < 10) return 10

    if (closestMultipleOfFive > 30) return 30

    return closestMultipleOfFive
  }

  public findBankrateSelectedRate(): Rate {
    const { rate: selectedRate, termInMonths: selectedTerm } =
      this.bankrateSelectedRate ?? {}

    if (selectedRate && selectedTerm) {
      const matchedRate = this.sortedRates.find(({ rate, term }) => {
        const termName = rateCodesMap[term]
        const termInMonths = deriveLoanTermInMonths(termName)

        return rate === selectedRate && termInMonths === selectedTerm
      })

      if (matchedRate) {
        return matchedRate
      }
    }

    return null
  }

  public findLowest(recommendedTerm: number, noPoints?: boolean): Rate {
    if (recommendedTerm > 30) {
      recommendedTerm = 30
    }
    if (recommendedTerm < 10) {
      recommendedTerm = 10
    }

    const lowestRate = this.sortedRates.find(({ term, discountPoints }) => {
      const termName = rateCodesMap[term]
      const numericTerm = parseInt(termName)

      if (noPoints) {
        return recommendedTerm === numericTerm && discountPoints <= 0
      }

      return recommendedTerm === numericTerm
    })

    if (lowestRate) {
      return lowestRate
    }

    return null
  }

  public zeroUpfrontRate(recommendedTerm: number): Rate {
    if (recommendedTerm > 30) {
      recommendedTerm = 30
    }
    if (recommendedTerm < 10) {
      recommendedTerm = 10
    }

    return this.sortedRates.find((rate) => {
      const termName = rateCodesMap[rate.term]
      const numericTerm = parseInt(termName)
      const remainingBalance = this.remainingBalance

      if (numericTerm === recommendedTerm && remainingBalance) {
        const loanCalcDecisions = {
          waiveEscrow: false,
          escrowRollIn: true,
          willRollInFees: true,
        }

        const loanCalcFinancials = {
          cashOut: rate.cashOut,
          totalFees: rate.loanEstimateSections['D'].total,
          totalEscrow: rate.loanEstimateSections['G'].total,
          lenderCredit: extractFeeByDescription(
            rate?.loanEstimateSections?.['J']?.fees,
            'Lender Credit'
          ),
          originationFee: extractFeeByDescription(
            rate?.loanEstimateSections?.['A'].fees,
            'Loan origination fee'
          ),
          taxAndPrepaids:
            rate.loanEstimateSections['E'].total +
            rate.loanEstimateSections['F'].total,
          remainingBalance,
        }
        const loanCostsCalculator = new LoanCostsCalculator(
          loanCalcFinancials,
          loanCalcDecisions
        )

        const cashToClose = loanCostsCalculator.getLoanCosts().cashToClose

        if (cashToClose <= 0) return rate
      }
      return null
    })
  }

  public findMaxCashOut() {}

  public findMatchedRates(): MatchedRates {
    const bankrateMatchedRate = this.findBankrateSelectedRate()
    if (bankrateMatchedRate) this.pushRates(bankrateMatchedRate, 'bankrate')

    switch (this.refiGoal) {
      case 'lowerPayment':
        this.findLowerMonthlyRates()
        break
      case 'lowerInterestRate':
        this.findLowerInterestRates()
        break
      case 'fasterPayoff':
        this.findFasterPayoffRates()
        break
      case 'cashOut':
        this.findCashOutRates()
        break
      case 'notSure':
      default:
        this.findNotSureRates()
        break
    }

    return this.matchedRates
  }

  private pushRates(rate: Rate, rateType: string) {
    if (!rate) return

    if (
      !this.matchedRates.rates.includes(rate) &&
      this.matchedRates.rates.length < 3
    ) {
      this.matchedRates.rates.push(rate)
      this.matchedRates.rateItems.push(rateType)
    }
  }

  private findLowerMonthlyRates() {
    const recommendedTerm = this.recommendedTerm

    this.pushRates(this.findLowest(30), this.refiGoal)

    this.pushRates(this.findLowest(30, true), 'noPoint')

    this.pushRates(this.findLowest(recommendedTerm), 'balanced')

    this.pushRates(this.findLowest(recommendedTerm + 10), this.refiGoal)
  }

  private findLowerInterestRates() {
    const recommendedTerm = this.recommendedTerm

    this.pushRates(this.findLowest(recommendedTerm), 'balanced')

    this.pushRates(this.findLowest(recommendedTerm, true), 'noPoint')

    this.pushRates(this.findLowest(15), this.refiGoal)

    this.pushRates(this.findLowest(10), this.refiGoal)
  }

  private findFasterPayoffRates() {
    this.pushRates(this.findLowest(15), this.refiGoal)

    this.pushRates(this.findLowest(15, true), 'noPoint')

    this.pushRates(this.findLowest(10), this.refiGoal)

    this.pushRates(this.findLowest(10, true), 'noPoint')
  }

  private findCashOutRates() {
    const recommendedTerm = this.recommendedTerm

    this.pushRates(this.findLowest(recommendedTerm), 'balanced')

    this.pushRates(this.findLowest(recommendedTerm - 5), this.refiGoal)

    this.pushRates(this.findLowest(recommendedTerm + 5), this.refiGoal)
  }

  private findNotSureRates() {
    const recommendedTerm = this.recommendedTerm

    this.pushRates(this.findLowest(recommendedTerm), 'balanced')

    this.pushRates(this.findLowest(recommendedTerm, true), 'noPoint')

    this.pushRates(this.findLowest(recommendedTerm - 10), 'fasterPayoff')

    this.pushRates(this.findLowest(recommendedTerm + 10), 'lowerPayment')
  }
}
